// @erp/module-cms/schemas — the PUBLICATIONS content type wire contracts. A
// document library entry: a PROSPECTUS, a BOOK, a SYLLABUS, a JOURNAL ISSUE, or an
// OTHER document. Each carries DOC-TYPE-SPECIFIC metadata (a book's ISBN/pages/edition;
// a syllabus' program/department/semester/…; an issue's journal/issn/frequency), a
// Latest/Outdated editorial badge, a cover image + a downloadable PDF (cms_media ids),
// an attachments[] list, structured contributors / highlights / tags (0018), and a
// companion-announcement auto-sync. Modelled on the 0005 tenders / 0006 events companion-sync
// slices (minus the ref-no engine + corrigendum linkage). Browser-safe: zod + plain
// data only (fe forms and the gateway parse the SAME schemas). Feature-referenced
// from MBSPSU (READ-ONLY, internally 'prospectus') — rebuilt for the target stack,
// English-first + locale-ready (a translation is a SIBLING ROW under a different
// `locale`, never a *_hi/*_pa column explosion).
//
// ┌──────────────────────────────────────────────────────────────────────────────┐
// │ SECURITY — body_html is stored RAW and is NOT sanitized on write (the 0004     │
// │ announcements law). Sanitization happens ON THE WAY OUT: every /public/*        │
// │ route passes bodyHtml through the allowlist sanitiser (api/sanitize.ts,         │
// │ wired 2026-09-13 per ADR-010 §2.4) before it leaves the ERP. Admin reads        │
// │ carry the raw column; content is authored ONLY by trusted cms:publications holders.│
// ├──────────────────────────────────────────────────────────────────────────────┤
// │ DEFERRED (documented, NOT built): the MBSPSU prospectus private_pin (a PIN +    │
// │ HMAC signed-asset gate) is the CROSS-CUTTING "private records" slice tenders/   │
// │ events/publications will share — so there is NO privatePin / visibilitySchedule │
// │ here and status is draft|published|archived only (no 'private' yet). Revisions  │
// │ + the public read-split are likewise deferred to their own waves.               │
// └──────────────────────────────────────────────────────────────────────────────┘
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs } from "@erp/core-schemas";
import { SLUG_MAX, SLUG_RE } from "./categories.js";
import { Locale } from "./locale.js";
import { AnnouncementAttachment, ATTACHMENTS_MAX, BODY_HTML_MAX, SUMMARY_MAX } from "./announcements.js";
import { HttpUrl } from "./href.js";
import { PinField } from "./private.js";
import { PublicationAccessLevel } from "./reading.js";
import { PublishingStatus } from "./settings.js";
import { Contributor, Contributors, Highlight, Highlights, Tags } from "./structured.js";

// NOTE: slugify + SLUG_MAX/SLUG_RE are REUSED from the categories slice (the ONE slug
// grammar + deriver) and BODY_HTML_MAX / the AnnouncementAttachment shape from the
// announcements slice — one source, never a re-declaration.

// ── The document-type vocabulary (the decided kinds — a bad type 400s) ─────────
// The MBSPSU documentType set (prospectus | book | syllabus | other) rebuilt as
// stable lower tokens, plus journal_issue (2026-09-13, A6: the ssneri.com archive is
// 41 issues of one journal — they stop being `other`). doc_type SELECTS the doc_meta
// shape (the discriminator below).
export const PUBLICATION_DOC_TYPES = ["prospectus", "book", "syllabus", "journal_issue", "other"] as const;
export const PublicationDocType = z.enum(PUBLICATION_DOC_TYPES);
export type PublicationDocType = z.infer<typeof PublicationDocType>;

// ── The active-status badge — an editor-set Latest | Outdated flag (the MBSPSU
// activeStatus). NOT derived (unlike the announcements is_new badge that decays with
// wall-clock time) — the editor marks which edition of a document is current. ─────────
export const PUBLICATION_ACTIVE_STATUSES = ["latest", "outdated"] as const;
export const PublicationActiveStatus = z.enum(PUBLICATION_ACTIVE_STATUSES);
export type PublicationActiveStatus = z.infer<typeof PublicationActiveStatus>;

// ── The BOOK catalogue fields (db-cms 0022, contract item 3): plain columns beside the
// doc_meta blob, so the site's catalogue can filter and price without parsing JSON. They
// are available on every row (a report in a series has a year and a price too); the admin
// form shows them for a book. `language` is a canonical BCP 47 tag (the machine-readable
// twin of doc_meta.language's display label); `pricePaise` is integer paise (the db-lib
// money law); `availability` is the ONE stock vocabulary; `obtainUrl` an absolute http(s)
// link to buy or request a copy; `notesPrivate` is ADMIN ONLY — never on a public DTO. ──
export const PUBLICATION_AVAILABILITIES = ["in_print", "out_of_print", "on_request"] as const;
export const PublicationAvailability = z.enum(PUBLICATION_AVAILABILITIES);
export type PublicationAvailability = z.infer<typeof PublicationAvailability>;

/** A publication year — a plausible four-digit year (the title's FoundedYear rule). */
export const PublicationYear = z.number().int().min(1000).max(2999);

// ── The publishing status enum — REUSED from settings (draft|published|archived),
// so the publication lifecycle and the site-wide default_publishing_status can never
// drift onto different value sets (the announcements/tenders/events precedent). ───────
export const PublicationStatus = PublishingStatus;
export type PublicationStatus = z.infer<typeof PublicationStatus>;

// ══════════════ DOC-TYPE-SPECIFIC metadata — a DISCRIMINATED union by doc_type ═══════════
// The doc-type-specific fields (the MBSPSU flat isbn/pages/edition/program/… columns)
// rebuilt as ONE structured JSON blob (doc_meta_json) validated by doc_type. Each
// variant is a STRICT object carrying its own `docType` literal discriminator, so a
// book's syllabus-only field (or vice-versa) is REJECTED at the edge — a stray key can
// never land in the blob. The COMMON fields (volume/issue/format/language/publisher/
// author) are available to EVERY type (a book has a publisher, a syllabus a scheme);
// only the truly type-specific fields are gated. All inner fields are optional (a
// partially-filled document form is valid — the tenders/events *_json law).
//
// The blob carries NO money field: a book's price is the `price_paise` COLUMN (0022,
// integer paise — the db-lib money law), beside the blob, so the catalogue can sort on it.
// `pages` is a non-negative INTEGER count (not a float).

/** Bounded free text — the max keeps a runaway value out of the blob. */
const metaText = (max: number) => z.string().trim().max(max);

/**
 * The common fields every publication type may carry (bibliographic display metadata).
 *
 * THE CONVENTION THE SITE RELIES ON (decided 2026-09-13, ADR-010 review C2 — the boundary
 * carries no types, so it must carry a convention): `volume` and `issue` are the BARE
 * identifiers — "2", "14", "3-4" — never a labelled display string ("Vol. 2", "खंड 2").
 * The consumer labels them in its own locale (the site's `taxonomy.volumeLabel` /
 * `issueLabel`); a label typed here would print twice. `author` is a `; `-separated list
 * of names ("Dr. Vidya Chand Thakur; Chetram Garg"), one credit per name, which the
 * consumer splits into contributors. The import (apps/workers/api/scripts) writes exactly
 * these shapes from the archive's JSON-LD volumeNumber/issueNumber and editor credits.
 */
const commonMetaFields = {
  /** The bare volume identifier ("14"), never "Vol. 14" — see above. */
  volume: metaText(50).optional(),
  /** The bare issue identifier ("1"), never "Issue 1" — see above. */
  issue: metaText(50).optional(),
  format: metaText(100).optional(),
  language: metaText(100).optional(),
  publisher: metaText(300).optional(),
  /** `; `-separated names, one credit each — see above. */
  author: metaText(300).optional(),
};

/** BOOK-only metadata (the MBSPSU book fields): ISBN, page count, edition. */
const bookMetaFields = {
  isbn: metaText(20).optional(),
  /** A non-negative INTEGER page count (never a float; whole-count discipline). */
  pages: z.number().int().min(0).optional(),
  edition: metaText(100).optional(),
};

/** SYLLABUS-only metadata (the MBSPSU syllabus fields): program → ltp. */
const syllabusMetaFields = {
  program: metaText(200).optional(),
  department: metaText(200).optional(),
  semester: metaText(50).optional(),
  courseCode: metaText(50).optional(),
  /** Free text — "4" or a structured "3-1-0" descriptor (a display label, not a count). */
  credits: metaText(50).optional(),
  scheme: metaText(100).optional(),
  branch: metaText(200).optional(),
  /** Lecture-Tutorial-Practical descriptor, e.g. "3-1-2" (a display label). */
  ltp: metaText(50).optional(),
};

/** JOURNAL ISSUE-only metadata (A6): the journal's name is REQUIRED (an issue of nothing is not an issue); issn / frequency optional. volume / issue / publisher ride the common set. */
const journalIssueMetaFields = {
  /** The journal's title as printed ("Itihas Divakar") — the one required field of the variant. */
  journal: z.string().trim().min(1).max(200),
  /** ISSN with or without the hyphen ("2277-2456") — bounded, not validated (the archive's is empty). */
  issn: metaText(20).optional(),
  /** Publication frequency label — "Half-yearly", "Quarterly", Django's 'HY' code (a display label, not a schedule). */
  frequency: metaText(50).optional(),
};

/** prospectus | other → the common set only (no type-specific fields). */
export const PublicationProspectusMeta = z.object({ docType: z.literal("prospectus"), ...commonMetaFields }).strict();
export const PublicationOtherMeta = z.object({ docType: z.literal("other"), ...commonMetaFields }).strict();
/** book → common + isbn/pages/edition. */
export const PublicationBookMeta = z
  .object({ docType: z.literal("book"), ...commonMetaFields, ...bookMetaFields })
  .strict();
/** syllabus → common + program/department/semester/courseCode/credits/scheme/branch/ltp. */
export const PublicationSyllabusMeta = z
  .object({ docType: z.literal("syllabus"), ...commonMetaFields, ...syllabusMetaFields })
  .strict();
/** journal_issue → common + journal (required) / issn / frequency. A book's isbn/pages/edition on it is REJECTED (strict). */
export const PublicationJournalIssueMeta = z
  .object({ docType: z.literal("journal_issue"), ...commonMetaFields, ...journalIssueMetaFields })
  .strict();

/**
 * The doc_meta discriminated union — `docType` selects the variant. A create/update
 * carries the FULL union (docType + its fields) so the doc_type column and the blob are
 * written together (never inconsistent). The strict variants reject a cross-type field
 * (a book with a `program`, a syllabus with an `isbn`) as a zod VALIDATION 400.
 */
export const PublicationDocMeta = z.discriminatedUnion("docType", [
  PublicationProspectusMeta,
  PublicationBookMeta,
  PublicationSyllabusMeta,
  PublicationJournalIssueMeta,
  PublicationOtherMeta,
]);
export type PublicationDocMeta = z.infer<typeof PublicationDocMeta>;

// ── The write-side field validators (one source, Create/Update both derive) ─────────
const publicationCore = {
  title: z.string().trim().min(1).max(300),
  /** The FORMAT rule only — the site's reserved section names (./reserved-slugs.ts) are judged by the service on the EFFECTIVE slug, explicit or derived → 422 CMS_SLUG_RESERVED. */
  slug: z.string().trim().min(1).max(SLUG_MAX).regex(SLUG_RE, "Slug must be lower-kebab (a-z, 0-9, hyphen)"),
  /** RAW HTML — sanitized ONLY at the website render (HTMLRewriter), never here. Nullable to clear. */
  bodyHtml: z.string().max(BODY_HTML_MAX).nullable(),
  summary: z.string().trim().max(SUMMARY_MAX).nullable(),
  status: PublicationStatus,
  /** Explicit publish instant (epoch-ms). Usually left unset — the service stamps it when status→published. Nullable to clear. */
  publishedAt: EpochMs.nullable(),
  /** FK to a cms_category of type='publication' — the service validates the cross-entity ref. Nullable to clear. */
  categoryId: EntityId.nullable(),
  /** The Latest/Outdated editorial badge (the MBSPSU activeStatus). */
  activeStatus: PublicationActiveStatus,
  /** cms_media id of the cover image (the R2 presign flow, 0003). Nullable to clear. */
  coverMediaId: EntityId.nullable(),
  /** cms_media id of the downloadable PDF (the R2 presign flow, 0003). Nullable to clear. */
  pdfMediaId: EntityId.nullable(),
  attachments: z.array(AnnouncementAttachment).max(ATTACHMENTS_MAX).nullable(),
  /** The doc-type-specific metadata (the discriminated blob; docType lives here as the discriminator). */
  docMeta: PublicationDocMeta,
  /** Structured credits (0018, A2 — schemas/structured.ts Contributor, ≤100). doc_meta.author stays the legacy `; `-joined fallback. Nullable to clear. */
  contributors: Contributors.nullable(),
  /** Structured "Key Highlights" (0018, A3 — schemas/structured.ts Highlight, ≤30). Nullable to clear. */
  highlights: Highlights.nullable(),
  /** Bare-string tags (0018, A4 — the gallery rule: ≤60 chars, ≤50 per row). Nullable to clear. */
  tags: Tags.nullable(),
  /** Locale-ready seam — v1 writes only 'en' (db-cms 0007). */
  /** The row's locale — a canonical BCP 47 tag (./locale.ts, the locale contract). */
  locale: Locale,
  /**
   * The PUBLICATION TITLE this row belongs to (db-cms 0022) — an issue's journal, a book's
   * series — by id; the service validates the ref (a live title of a kind that fits the
   * docType). Nullable to unlink. The issue form derives doc_meta.journal / issn / frequency
   * from the linked title; the columns stay so the site's convention is unchanged.
   */
  titleId: EntityId.nullable(),
  /** The book's language as a canonical BCP 47 tag ('hi', 'en', 'sa') — see PUBLICATION_AVAILABILITIES above. Nullable to clear. */
  language: Locale.nullable(),
  /** The year of publication (a book's imprint year). Nullable to clear. */
  year: PublicationYear.nullable(),
  /** The list price in integer PAISE (₹250 → 25000); null = not for sale / unknown. */
  pricePaise: z.number().int().min(0).nullable(),
  availability: PublicationAvailability.nullable(),
  /** Where to buy or request a copy — an absolute http(s) URL. Nullable to clear. */
  obtainUrl: HttpUrl.nullable(),
  /** ADMIN ONLY — plain text an editor keeps for the desk; never on a public DTO. Nullable to clear. */
  notesPrivate: z.string().max(BODY_HTML_MAX).nullable(),
  /**
   * The publication's copy in the LIBRARY (db-cms 0024, ADR-010 §2.12) — an OPAQUE LIB ebook
   * id the CMS stores and never joins (the admin shell fills the picker only when the install
   * runs `lib`; the public wire turns it into `readAccess.libraryUrl`). Nullable to clear.
   */
  libraryEbookId: EntityId.nullable(),
};

/**
 * Create body — title + docMeta required (docMeta.docType selects the kind and its
 * fields). slug OPTIONAL (the service derives it via slugify(title), id-fallback on a
 * non-sluggable title). status/locale/activeStatus carry the DDL defaults; published_at
 * is normally derived from the status. createAnnouncement is a ROUTING flag (not a
 * column) — publish auto-posts a companion announcement unless it is false (the tenders/
 * events precedent; default on).
 */
export const CreateCmsPublication = z.object({
  title: publicationCore.title,
  slug: publicationCore.slug.optional(),
  bodyHtml: publicationCore.bodyHtml.optional(),
  summary: publicationCore.summary.optional(),
  status: publicationCore.status.default("draft"),
  publishedAt: publicationCore.publishedAt.optional(),
  categoryId: publicationCore.categoryId.optional(),
  activeStatus: publicationCore.activeStatus.default("outdated"),
  coverMediaId: publicationCore.coverMediaId.optional(),
  pdfMediaId: publicationCore.pdfMediaId.optional(),
  attachments: publicationCore.attachments.optional(),
  docMeta: publicationCore.docMeta,
  contributors: publicationCore.contributors.optional(),
  highlights: publicationCore.highlights.optional(),
  tags: publicationCore.tags.optional(),
  locale: publicationCore.locale.default("en"),
  titleId: publicationCore.titleId.optional(),
  language: publicationCore.language.optional(),
  year: publicationCore.year.optional(),
  pricePaise: publicationCore.pricePaise.optional(),
  availability: publicationCore.availability.optional(),
  obtainUrl: publicationCore.obtainUrl.optional(),
  notesPrivate: publicationCore.notesPrivate.optional(),
  libraryEbookId: publicationCore.libraryEbookId.optional(),
  createAnnouncement: z.boolean().optional(),
  // Private-records slice (0011): access_level + the WRITE-ONLY pin (see tenders.ts) — since
  // 0024 the level is public | MEMBERS | private (schemas/reading.ts). OPTIONAL, with NO
  // schema default (V-API-5, 2026-09-21): the product default is `reading.defaultAccess`,
  // a SETTING the schema cannot see, and a `.default("public")` here made every caller that
  // omitted the field — an import, a script, an old client — mint a world-readable issue on
  // an install whose setting says members. The SERVICE resolves an absent level from the
  // setting (PublicationCtx.defaultAccess, read by the router per request); the FORM also
  // starts a new publication on it, so the two never disagree about what "unspecified" means.
  accessLevel: PublicationAccessLevel.optional(),
  pin: PinField.optional(),
});
export type CreateCmsPublication = z.infer<typeof CreateCmsPublication>;

/**
 * Update body — a partial PATCH over the same fields (slug stays STABLE on rename; a
 * slug collision still maps to 409). A supplied docMeta REPLACES the doc_type + blob
 * together (the full discriminated union), so the two never drift; omitting it keeps
 * the stored kind.
 */
export const UpdateCmsPublication = z
  .object({
    title: publicationCore.title,
    slug: publicationCore.slug,
    bodyHtml: publicationCore.bodyHtml,
    summary: publicationCore.summary,
    status: publicationCore.status,
    publishedAt: publicationCore.publishedAt,
    categoryId: publicationCore.categoryId,
    activeStatus: publicationCore.activeStatus,
    coverMediaId: publicationCore.coverMediaId,
    pdfMediaId: publicationCore.pdfMediaId,
    attachments: publicationCore.attachments,
    docMeta: publicationCore.docMeta,
    contributors: publicationCore.contributors,
    highlights: publicationCore.highlights,
    tags: publicationCore.tags,
    locale: publicationCore.locale,
    titleId: publicationCore.titleId,
    language: publicationCore.language,
    year: publicationCore.year,
    pricePaise: publicationCore.pricePaise,
    availability: publicationCore.availability,
    obtainUrl: publicationCore.obtainUrl,
    notesPrivate: publicationCore.notesPrivate,
    libraryEbookId: publicationCore.libraryEbookId,
    createAnnouncement: z.boolean(),
    // Private-records slice (0011): flip access_level / (re)set the write-only pin (tenders.ts);
    // public | members | private since 0024.
    accessLevel: PublicationAccessLevel,
    pin: PinField,
  })
  .partial();
export type UpdateCmsPublication = z.infer<typeof UpdateCmsPublication>;

/** List query — keyset cursor+limit + optional ?docType ?status ?categoryId ?activeStatus ?locale filters (soft-deleted rows always excluded). */
export const CmsPublicationListQuery = CursorQuery.extend({
  docType: PublicationDocType.optional(),
  status: PublicationStatus.optional(),
  categoryId: EntityId.optional(),
  activeStatus: PublicationActiveStatus.optional(),
  /** Exact-eq on the row's locale (0018) — the admin list narrows to one locale's rows; omitted lists every locale. */
  locale: Locale.optional(),
  /** Exact-eq on the linked title (0022) — a title's archive; omitted lists every row. */
  titleId: EntityId.optional(),
  /** Exact-eq on the book's year (0022) — the catalogue's year filter. */
  year: z.coerce.number().int().min(1000).max(2999).optional(),
  /** Opt-in TRASH view — true lists soft-deleted rows only; omitted (the default) lists live rows. Coerced + optional so a stray value never 422s the query. */
  // Self-healing like the FE's ?trashed: only "1"/"true" opt into trash; anything else
  // (absent, "false", "0", garbage) heals to the live list — never flips to trash or 422s.
  trashed: z
    .enum(["1", "true"])
    .transform(() => true)
    .optional()
    .catch(undefined),
});
export type CmsPublicationListQuery = z.infer<typeof CmsPublicationListQuery>;

/**
 * The wire DTO — the read shape (camelCase; audit actor columns stay server-side).
 * `docType` surfaces from the column; `docMeta` is the parsed discriminated blob;
 * `attachments` is the parsed list; `deletedAt` surfaces so the admin trash view can
 * tell live rows from trashed ones.
 */
export const CmsPublication = z.object({
  id: EntityId,
  slug: z.string(),
  locale: z.string(),
  docType: PublicationDocType,
  title: z.string(),
  bodyHtml: z.string().nullable(),
  summary: z.string().nullable(),
  status: PublicationStatus,
  publishedAt: EpochMs.nullable(),
  categoryId: EntityId.nullable(),
  docMeta: PublicationDocMeta.nullable(),
  activeStatus: PublicationActiveStatus,
  coverMediaId: EntityId.nullable(),
  pdfMediaId: EntityId.nullable(),
  attachments: z.array(AnnouncementAttachment),
  /** 0018 — the parsed contributors list ([] when none). */
  contributors: z.array(Contributor),
  /** 0018 — the parsed highlights list ([] when none). */
  highlights: z.array(Highlight),
  /** 0018 — the parsed tags list ([] when none). */
  tags: z.array(z.string()),
  companionAnnouncementId: EntityId.nullable(),
  // Private-records slice (0011): the STORED access level (public | members | private since
  // 0024 — the public shape carries the EFFECTIVE one under `readAccess.level`) + a DERIVED
  // lock flag (private only); never pin/pin_hash (tenders.ts).
  accessLevel: PublicationAccessLevel,
  isLocked: z.boolean(),
  /** 0022 — the linked publication title's id (null when standalone). The PUBLIC shape resolves it to `title: { slug, name, kind }`. */
  titleId: EntityId.nullable(),
  /** 0022 — the book catalogue fields (null when unknown / not a book). */
  language: z.string().nullable(),
  year: z.number().int().nullable(),
  pricePaise: z.number().int().nullable(),
  availability: PublicationAvailability.nullable(),
  obtainUrl: z.string().nullable(),
  /** 0022 — ADMIN ONLY; the public shape OMITS this key entirely (schemas/public.ts). */
  notesPrivate: z.string().nullable(),
  /** 0024 — the opaque LIB ebook id (null when the library holds no copy). The public shape resolves it to `readAccess.libraryUrl`. */
  libraryEbookId: EntityId.nullable(),
  createdAt: EpochMs,
  updatedAt: EpochMs,
  deletedAt: EpochMs.nullable(),
});
export type CmsPublication = z.infer<typeof CmsPublication>;
